import { useRef, useState } from "react";
import { FileSearch, MapPin, Upload, Warehouse } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useImportJob } from "@/hooks/useImportJob";
import { ImportProgress } from "@/components/ImportProgress";

function AddressList({ addresses, testid }) {
  if (!addresses.length) {
    return <span className="text-xs text-[#8A9A90]" data-testid={`${testid}-empty`}>Não localizado</span>;
  }
  return (
    <div className="flex flex-wrap gap-1" data-testid={testid}>
      {addresses.map((address) => (
        <span
          key={address}
          className="bg-[#EAF7EF] text-[#0F6B3A] border border-[#B9E8CA] px-1.5 py-0.5 text-[11px] font-mono"
        >
          {address}
        </span>
      ))}
    </div>
  );
}

export function ConsultaAbastecimento() {
  const inputRef = useRef(null);
  const [consulta, setConsulta] = useState(null);
  const carregarConsulta = async (resultado) => {
    try {
      const { data } = await api.get(
        `/enderecos/consulta-abastecimento/${resultado.consulta_id}`,
      );
      setConsulta(data);
      toast.success(`${resultado.itens} SKUs localizados para consulta`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao carregar consulta de endereços");
    }
  };
  const { job, start, running } = useImportJob(carregarConsulta);
  const iniciarImportacao = () => inputRef.current?.click();
  const selecionarArquivo = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      setConsulta(null);
      start("/enderecos/consulta-abastecimento/import", file);
    }
    event.target.value = "";
  };

  return (
    <section
      className="border-y border-[#D9E3DC] py-5"
      data-testid="consulta-abastecimento-section"
    >
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex h-10 w-10 items-center justify-center bg-[#EAF7EF]">
            <FileSearch className="h-5 w-5 text-[#0F6B3A]" />
          </div>
          <div>
            <h2 className="font-bold text-[#0F2A1F]">Consultar endereços do Abastecimento</h2>
            <p className="text-xs text-[#5A6B62]">
              PDF, CSV ou XLSX com COD, DESCRIÇÃO e QUANTIDADE — saldos, Pulmão e Separação lado a lado.
            </p>
          </div>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept=".pdf,.csv,.xlsx,.xlsm,.txt,application/pdf,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          className="hidden"
          data-testid="consulta-abastecimento-input"
          onChange={selecionarArquivo}
        />
        <button
          data-testid="consulta-abastecimento-import-btn"
          disabled={running}
          onClick={iniciarImportacao}
          className="inline-flex items-center justify-center gap-2 rounded-md bg-[#0F6B3A] px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#0B5530] disabled:opacity-60"
        >
          <Upload className="h-4 w-4" />
          Importar Abastecimento
        </button>
      </div>

      <ImportProgress job={job} testid="consulta-abastecimento-progress" />

      {consulta && (
        <div className="mt-5" data-testid="consulta-abastecimento-result">
          <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[#5A6B62]">
            <span className="font-mono font-bold text-[#0F2A1F]">{consulta.itens.length} SKUs</span>
            <span data-testid="consulta-abastecimento-file">{consulta.arquivo}</span>
            <span className="inline-flex items-center gap-1 text-[#0F6B3A]">
              <Warehouse className="h-3.5 w-3.5" /> Pulmão
            </span>
            <span className="inline-flex items-center gap-1 text-[#0F6B3A]">
              <MapPin className="h-3.5 w-3.5" /> Separação
            </span>
          </div>
          <div className="border border-[#D9E3DC]" data-testid="consulta-abastecimento-list">
            <div className="hidden grid-cols-[92px_minmax(180px,1fr)_70px_74px_82px_110px_1fr_1fr] gap-3 bg-[#F4F8F5] px-3 py-2 text-[10px] font-bold uppercase text-[#0F2A1F] md:grid">
              <span>Código</span>
              <span>Produto</span>
              <span>Qtd.</span>
              <span>Físico</span>
              <span>Alocado</span>
              <span>Disponível</span>
              <span>Pulmão</span>
              <span>Separação</span>
            </div>
            {consulta.itens.map((item) => (
              <article
                key={item.codigo}
                className="grid gap-2 border-t border-[#F1F5F2] px-3 py-3 md:grid-cols-[92px_minmax(180px,1fr)_70px_74px_82px_110px_1fr_1fr] md:gap-3"
                data-testid={`consulta-abastecimento-row-${item.codigo}`}
              >
                <div className="font-mono text-sm font-bold text-[#0F6B3A]">{item.codigo}</div>
                <div className="min-w-0 text-sm font-semibold text-[#0F2A1F] md:font-normal">
                  {item.descricao}
                </div>
                <div className="font-mono text-sm text-[#0F2A1F]">Qtd. {item.quantidade}</div>
                <div
                  className="font-mono text-sm text-[#0F2A1F]"
                  data-testid={`consulta-abastecimento-${item.codigo}-fisico`}
                >
                  Físico {item.quantidade_fisica ?? 0}
                </div>
                <div
                  className="font-mono text-sm font-bold text-[#0F6B3A]"
                  data-testid={`consulta-abastecimento-${item.codigo}-alocado`}
                >
                  Alocado {item.quantidade_alocada ?? 0}
                </div>
                <div
                  className="font-mono text-sm font-bold text-[#0F6B3A]"
                  data-testid={`consulta-abastecimento-${item.codigo}-disponivel`}
                >
                  Disponível {item.quantidade_disponivel ?? 0}
                </div>
                <div>
                  <div className="mb-1 text-[10px] font-bold uppercase text-[#5A6B62] md:hidden">Pulmão</div>
                  <AddressList
                    addresses={item.pulmao}
                    testid={`consulta-abastecimento-${item.codigo}-pulmao`}
                  />
                </div>
                <div>
                  <div className="mb-1 text-[10px] font-bold uppercase text-[#5A6B62] md:hidden">Separação</div>
                  <AddressList
                    addresses={item.separacao}
                    testid={`consulta-abastecimento-${item.codigo}-separacao`}
                  />
                </div>
              </article>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}